// src/app/core/auth/permission.service.ts
import { Injectable, signal, computed } from '@angular/core';

/**
 * PermissionService — fuente de verdad para los permisos del usuario.
 *
 * Los permisos se cargan del array `permissions` del AuthResponse
 * y se almacenan en un Signal privado.
 * Se limpian al hacer logout.
 *
 * Uso en templates vía directiva:
 *   <button *hasPermission="'UPDATE_LOAN'">Editar</button>
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly _permissions = signal<Set<string>>(new Set());

  /** Signal público de solo lectura para inspeccionar permisos */
  readonly permissionCount = computed(() => this._permissions().size);

  /**
   * Carga los permisos tras un login exitoso.
   * Llamado exclusivamente por AuthService.
   * @internal
   */
  setPermissions(permissions: string[]): void {
    this._permissions.set(new Set(permissions));
  }

  /**
   * Limpia todos los permisos al hacer logout.
   * Llamado exclusivamente por AuthService.
   * @internal
   */
  clearPermissions(): void {
    this._permissions.set(new Set());
  }

  /** Verifica si el usuario tiene un permiso específico */
  hasPermission(permission: string): boolean {
    return this._permissions().has(permission);
  }

  /** Verifica si el usuario tiene al menos uno de los permisos listados */
  hasAnyPermission(permissions: string[]): boolean {
    const current = this._permissions();
    return permissions.some(p => current.has(p));
  }

  /** Verifica si el usuario tiene todos los permisos listados */
  hasAllPermissions(permissions: string[]): boolean {
    const current = this._permissions();
    return permissions.every(p => current.has(p));
  }
}
